import React, { useState } from "react";
import tick from "../assets/images/tick.svg";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../hooks/use-toast";
import Toaster from "./ui/toaster";

const AllSet = () => {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);

  const handleViewDashboard = async () => {
    setIsLoading(true);
    try {
      console.log("Refreshing user profile before dashboard navigation...");

      // Step 1: Refresh user profile from backend to get latest data
      const freshUser = await refreshUser();

      if (!freshUser) {
        throw new Error("Failed to fetch user profile");
      }

      console.log("Fresh user data:", {
        kycStatus: freshUser.kycStatus,
        veriffStatus: freshUser.veriffDetails?.status,
        profileCompleted: freshUser.profile_completed,
      });

      // Step 2: Verify KYC/Veriff status is approved
      const kycStatus = freshUser.veriffDetails?.status || freshUser.kycStatus;

      if (kycStatus !== "submitted" && kycStatus !== "approved") {
        console.error("KYC not approved:", kycStatus);
        toast({
          description:
            "Identity verification is not yet approved. Please complete verification.",
          variant: "error",
        });
        return;
      }

      // Step 3: Verify profile is marked as complete
      if (!freshUser.profile_completed) {
        console.log("Profile not marked complete yet, retrying in 1 second...");

        // Backend might need a moment to update, retry once after 1 second
        await new Promise((resolve) => setTimeout(resolve, 1000));

        const retryUser = await refreshUser();

        if (!retryUser?.profile_completed) {
          console.error("Profile completion still pending after retry");
          toast({
            description:
              "Your profile is being finalized. Please try again in a moment.",
            variant: "warning",
          });
          return;
        }

        console.log("Profile marked complete after retry");
      }

      // Step 4: All checks passed, navigate to dashboard
      console.log("All checks passed, navigating to dashboard");
      navigate("/", { replace: true });
    } catch (error) {
      console.error("Error in handleViewDashboard:", error);
      toast({
        description: error.message || "Something went wrong. Please try again.",
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="flex justify-center min-h-[100vh] bg-white items-center fixed top-0 w-full z-[100]">
        <div className="lg:w-[358px] w-full px-6 lg:px-0 flex flex-col items-center justify-center gap-4">
          <img src={tick} className="w-[194px]" alt="Success" />
          <p className="text-[#33125F] font-semibold text-[32px]">
            You're all set!
          </p>
          <p className="text-[#423E51] text-[16px]">Let's make money moves.</p>
          <button
            className={`bg-[linear-gradient(86.72deg,#57249C_-10.66%,#EB3366_113.47%)] mt-7 text-white flex items-center justify-center font-semibold text-[14px] font-[400] px-8 lg:py-[11px] py-[12px] rounded w-[308px] lg:w-[358px] rounded-full transition duration-300 ease-in-out ${
              isLoading ? "opacity-70 cursor-not-allowed" : "hover:scale-[1.1]"
            }`}
            onClick={handleViewDashboard}
            disabled={isLoading}
          >
            {isLoading ? (
              <span className="flex items-center gap-2">
                <svg
                  className="animate-spin h-5 w-5"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Verifying...
              </span>
            ) : (
              "View dashboard"
            )}
          </button>
        </div>
      </div>
      <Toaster />
    </>
  );
};

export default AllSet;
